import { useWindowSize } from "@jutro/hooks";
import {
  AddCircleIcon,
  Button,
  Input,
  MessageBox,
  SearchIcon,
  Table,
} from "@jutro/ui";
import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import { useAtom } from "jotai";
import { nanoid } from "nanoid";
import { select } from "radash";
import { useEffect, useMemo, useState } from "react";
import { chosenMedicalRecordsAtom } from "components/TagFormNew/Sections/atoms";
import { ActionButton } from "components/new/MedicalDocumentation/ActionButton";
import { selectedMedicalRecordsAtom } from "components/new/MedicalDocumentation/atoms";
import { dateLabelMap } from "components/new/MedicalDocumentation/tools";
import { DoctorMSMedicalRecordEntity } from "lib/graphql/megaSchema";
import { isDataTile } from "lib/tools/createWorkspace/tools";
import { DataTile, TileColumn } from "lib/tools/createWorkspace/types";
import { useTileSystem } from "lib/tools/createWorkspace/useTileSystem";

type Props = {
  data: DoctorMSMedicalRecordEntity[];
  withRowActions?: boolean;
  isDisplayOnly?: boolean;
};

export const MedicalDocumentationTable = ({
  data,
  isDisplayOnly = false,
  withRowActions = false,
}: Props) => {
  const { addTile, removeTile, visibleTiles } = useTileSystem();
  const [searchText, setSearchText] = useState("");
  const [selectedMedicalRecords, setSelectedMedicalRecords] = useAtom(
    selectedMedicalRecordsAtom,
  );
  const [chosenMedicalRecords, setChosenMedicalRecords] = useAtom(
    chosenMedicalRecordsAtom,
  );

  const [openedRow, setOpenedRow] =
    useState<DoctorMSMedicalRecordEntity | null>(null);

  const isTableDisabled =
    visibleTiles.find((vt) => isDataTile(vt) && vt.name === "tag-form") &&
    withRowActions;

  const createNewTag = (row?: DoctorMSMedicalRecordEntity) => {
    const id = nanoid();

    if (row) {
      setChosenMedicalRecords([row]);
    } else {
      setChosenMedicalRecords(selectedMedicalRecords);
    }

    setSelectedMedicalRecords([]);

    // make sure there is no select file tile from previous tag creation/update
    removeTile("medical-record-file-select");

    addTile(
      {
        id,
        name: "tag-form",
        data: {
          id,
          type: "create",
          tag: {
            type: undefined,
            expirationAt: dayjs().add(1, "year"),
            confirmationType: "DOCUMENTATION",
          },
        },
      },
      TileColumn.Center,
    );
  };

  useEffect(() => {
    const currentFilePreview = visibleTiles.find(
      (tile) => isDataTile(tile) && tile.name === "file-preview",
    );

    if (!currentFilePreview) {
      setOpenedRow(null);
    }
  }, [visibleTiles]);

  useEffect(() => {
    if (!openedRow) return;

    const currentFilePreviewIndex = visibleTiles.findIndex(
      (tile) => isDataTile(tile) && tile.name === "file-preview",
    );

    if (currentFilePreviewIndex >= 0) {
      removeTile((visibleTiles[currentFilePreviewIndex] as DataTile).id);
    }

    addTile(
      {
        name: "file-preview",
        data: { fileId: openedRow.fileId },
        id: openedRow.fileId,
      },
      ({ visibleTiles }) =>
        visibleTiles.includes("visit-procedure-panel") ||
        currentFilePreviewIndex === 0
          ? TileColumn.Left
          : TileColumn.Right,
    );
  }, [openedRow]);

  const [width] = useWindowSize();

  const columns = useMemo<ColumnDef<DoctorMSMedicalRecordEntity>[]>(
    () => [
      {
        header: "Nazwa pliku",
        cell: ({ row }) => {
          return (
            <span
              style={{
                width: width / (isDisplayOnly || !withRowActions ? 6 : 7),
              }}
              className={`font-paragraph-1 h-full cursor-default truncate text-ellipsis align-middle text-jutro-new-warm-gray-800`}
              dangerouslySetInnerHTML={{
                __html: searchText
                  ? (
                      row.original.tidyDocumentationResult?.name ??
                      row.original.name
                    ).replace(
                      new RegExp(searchText, "gi"),
                      (s) => `<span class="bg-jutro-new-blue-300">${s}</span>`,
                    )
                  : (row.original.tidyDocumentationResult?.name ??
                    row.original.name),
              }}
            />
          );
        },
      },
      {
        header: width > 1400 ? "Data załączenia" : "Data",
        cell: ({ row }) => (
          <div className="font-paragraph-1 max-w-min pt-0.5">
            {dayjs(row.original.createdAt).format("YYYY‑MM‑DD")}
          </div>
        ),
      },
      ...(isDisplayOnly || !withRowActions
        ? []
        : [
            {
              accessorKey: "_",
              header: "",

              cell: ({
                row,
              }: {
                row: { original: DoctorMSMedicalRecordEntity };
              }) => {
                if (isTableDisabled) return null;

                return (
                  <div
                    className={`jm-opacity-0 flex h-4 max-w-min gap-3 ${selectedMedicalRecords.length > 0 ? "pointer-events-none" : "group-hover:jm-opacity-100"}`}
                  >
                    <Button
                      icon={<AddCircleIcon />}
                      label="Dodaj plik"
                      size="condensed"
                      variant="secondary"
                      tooltipPosition="top"
                      onClick={(e) => {
                        e.stopPropagation();
                        createNewTag(row.original);
                      }}
                    />

                    <ActionButton rows={[row.original]} />
                  </div>
                );
              },
            },
          ]),
    ],
    [searchText, selectedMedicalRecords, isTableDisabled, visibleTiles, width],
  );

  const eloMedRec = useMemo(
    () =>
      data.filter((md) =>
        isDisplayOnly
          ? true
          : !md.tagId ||
            (!withRowActions &&
              chosenMedicalRecords.find((ch) => ch.tagId === md.tagId)),
      ),
    [data],
  );

  const filteredMedicalRecords = useMemo(() => {
    return eloMedRec.filter((md) =>
      (md.tidyDocumentationResult?.name ?? md.name)
        .toLowerCase()
        .includes(searchText.toLowerCase()),
    );
  }, [eloMedRec, searchText]);

  useEffect(() => {
    const validIds = new Set(filteredMedicalRecords.map((rec) => rec.id));

    const updatedSelectedMedicalRecords = select(
      selectedMedicalRecords,
      (f) => f,
      (row) => validIds.has(row.id),
    );

    setSelectedMedicalRecords(updatedSelectedMedicalRecords);
  }, [filteredMedicalRecords]);

  useEffect(() => {
    if (filteredMedicalRecords.length === 0 && searchText.length > 0) {
      setSearchText("");
    }
  }, [eloMedRec]);

  const tableProps = {
    ...(isDisplayOnly
      ? ({
          selectionRowMode: "none",
          withOpen: true,
          openedRow,
          setOpenedRow,
        } as const)
      : ({
          selectionRowMode: "multiple",
          selectedRows: isTableDisabled ? [] : selectedMedicalRecords,
          setSelectedRows: setSelectedMedicalRecords,
          withOpen: true,
          openedRow,
          setOpenedRow,
          selectedRowsHeader: withRowActions ? (
            <div className="flex gap-3">
              <Button
                icon={<AddCircleIcon />}
                text="Otaguj&nbsp;pliki"
                size="condensed"
                variant="secondary"
                onClick={() => createNewTag()}
              />

              <ActionButton
                multiple
                rows={selectedMedicalRecords}
                setSelectedRows={setSelectedMedicalRecords}
              />
            </div>
          ) : null,
        } as const)),
  };

  const enhancedFilteredMedicalRecords = filteredMedicalRecords.map((row) => {
    return {
      ...row,
      tooltip: (
        <div className="flex max-h-[488px] max-w-[552px] flex-col gap-3 overflow-scroll rounded-lg px-4 pt-4 text-jutro-new-warm-gray-800">
          <div className="font-heading-4">
            {row.tidyDocumentationResult?.name ?? row.name}
          </div>

          <div>
            <div className="font-paragraph-2">
              {row.tidyDocumentationResult?.type &&
              row.tidyDocumentationResult.type in dateLabelMap
                ? dateLabelMap[row.tidyDocumentationResult.type]
                : "Data"}
            </div>
            <div className="font-paragraph-1">
              {dayjs(
                row.tidyDocumentationResult?.issuedAt ?? row.createdAt,
              ).format("YYYY-MM-DD")}
            </div>
          </div>

          {row.tidyDocumentationResult?.summary &&
            row.tidyDocumentationResult.summary.length > 0 && (
              <div className="flex flex-col gap-3">
                {row.tidyDocumentationResult.summary.map(
                  (e: { header: string; content: string }) => (
                    <div key={e.content}>
                      <div className="font-paragraph-2">{e.header}</div>
                      <div className="font-paragraph-1">{e.content}</div>
                    </div>
                  ),
                )}
              </div>
            )}
        </div>
      ),
    };
  });

  if (data.length === 0 && withRowActions) {
    return <MessageBox text="Brak plików" color="warmGray" />;
  }

  return (
    <div className="flex flex-col gap-4">
      {!isDisplayOnly && (
        <Input
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sideElement={<SearchIcon size="sm" />}
          placeholder="Przeszukaj załączone pliki"
          sideElementPosition="start"
        />
      )}

      {filteredMedicalRecords.length > 0 ? (
        <Table
          columns={columns}
          data={enhancedFilteredMedicalRecords}
          disabled={Boolean(isTableDisabled)}
          size="condensed"
          getIsRowLoading={(row) => row.tidy_status === "INPROGRESS"}
          {...tableProps}
        />
      ) : (
        <MessageBox text="Brak plików" color="warmGray" />
      )}
    </div>
  );
};
